import { useCallback, useRef } from 'react'

/**
 * Shared touch gesture helpers for mobile BottomSheet interactions.
 *
 * - Header vertical swipe:
 *   up   -> expand
 *   down -> minimize or close (if already minimized)
 * - Body horizontal swipe:
 *   left/right -> navigate next/prev item
 */
export function useBottomSheetGestures({
  isMinimized,
  onExpand,
  onMinimize,
  onClose,
  onSwipePrev,
  onSwipeNext,
  onBodySwipeMove,
  onHeaderSwipeMove,
  onHeaderSwipeEnd,
  minHeaderSwipe = 24,
  minHeaderCloseSwipe = 88,
  minBodySwipe = 48,
  maxHeaderDrag = 240,
  headerRubberBand = 0.24,
}) {
  const headerTouchStartRef = useRef(null)
  const headerPointerStartRef = useRef(null)
  const headerPointerIdRef = useRef(null)
  const bodyTouchStartRef = useRef(null)

  function blockTouchEvent(event) {
    event.stopPropagation()
  }

  function getHeaderDragOffset(dy, limit, rubberBand) {
    if (dy <= 0) {
      return dy * rubberBand
    }

    return Math.min(dy, limit)
  }

  const finishHeaderGesture = useCallback((dx, dy) => {
    let action = 'reset'

    if (Math.abs(dy) < minHeaderSwipe || Math.abs(dy) < Math.abs(dx)) {
      onHeaderSwipeEnd?.(action)
      onHeaderSwipeMove?.(0)
      return
    }

    if (dy > 0) {
      if (isMinimized || dy >= minHeaderCloseSwipe) {
        action = 'close'
        onClose?.()
      } else {
        action = 'minimize'
        onMinimize?.()
      }
    } else {
      action = 'expand'
      onExpand?.()
    }

    onHeaderSwipeEnd?.(action)
    onHeaderSwipeMove?.(0)
  }, [isMinimized, minHeaderCloseSwipe, minHeaderSwipe, onClose, onExpand, onHeaderSwipeEnd, onHeaderSwipeMove, onMinimize])

  const onHeaderTouchStart = useCallback((event) => {
    const touch = event.touches?.[0]
    if (!touch) return

    blockTouchEvent(event)
    headerTouchStartRef.current = { x: touch.clientX, y: touch.clientY }
  }, [])

  const onHeaderTouchMove = useCallback((event) => {
    const start = headerTouchStartRef.current
    const touch = event.touches?.[0]
    if (!start || !touch) return

    const dx = touch.clientX - start.x
    const dy = touch.clientY - start.y

    blockTouchEvent(event)

    const dragOffset = getHeaderDragOffset(dy, maxHeaderDrag, headerRubberBand)
    onHeaderSwipeMove?.(dragOffset)
  }, [headerRubberBand, maxHeaderDrag, onHeaderSwipeMove])

  const onHeaderTouchEnd = useCallback((event) => {
    const start = headerTouchStartRef.current
    const touch = event.changedTouches?.[0]
    headerTouchStartRef.current = null
    blockTouchEvent(event)

    if (!start || !touch) {
      onHeaderSwipeEnd?.('reset')
      onHeaderSwipeMove?.(0)
      return
    }

    const dy = touch.clientY - start.y
    const dx = touch.clientX - start.x
    finishHeaderGesture(dx, dy)
  }, [finishHeaderGesture, onHeaderSwipeEnd, onHeaderSwipeMove])

  const onHeaderPointerDown = useCallback((event) => {
    if (event.pointerType === 'mouse') return

    blockTouchEvent(event)
    headerPointerIdRef.current = event.pointerId
    headerPointerStartRef.current = { x: event.clientX, y: event.clientY }
    event.currentTarget?.setPointerCapture?.(event.pointerId)
  }, [])

  const onHeaderPointerMove = useCallback((event) => {
    if (event.pointerType === 'mouse') return
    if (headerPointerIdRef.current !== event.pointerId) return

    const start = headerPointerStartRef.current
    if (!start) return

    blockTouchEvent(event)

    const dy = event.clientY - start.y
    const dragOffset = getHeaderDragOffset(dy, maxHeaderDrag, headerRubberBand)
    onHeaderSwipeMove?.(dragOffset)
  }, [headerRubberBand, maxHeaderDrag, onHeaderSwipeMove])

  const onHeaderPointerEnd = useCallback((event) => {
    if (event.pointerType === 'mouse') return
    if (headerPointerIdRef.current !== event.pointerId) return

    const start = headerPointerStartRef.current
    headerPointerIdRef.current = null
    headerPointerStartRef.current = null
    blockTouchEvent(event)
    event.currentTarget?.releasePointerCapture?.(event.pointerId)

    if (!start) {
      onHeaderSwipeEnd?.('reset')
      onHeaderSwipeMove?.(0)
      return
    }

    const dx = event.clientX - start.x
    const dy = event.clientY - start.y
    finishHeaderGesture(dx, dy)
  }, [finishHeaderGesture, onHeaderSwipeEnd, onHeaderSwipeMove])

  const onHeaderPointerCancel = useCallback((event) => {
    if (headerPointerIdRef.current !== event.pointerId) return
    headerPointerIdRef.current = null
    headerPointerStartRef.current = null
    onHeaderSwipeEnd?.('reset')
    onHeaderSwipeMove?.(0)
  }, [onHeaderSwipeEnd, onHeaderSwipeMove])

  const onBodyTouchStart = useCallback((event) => {
    const touch = event.touches?.[0]
    if (!touch) return
    bodyTouchStartRef.current = { x: touch.clientX, y: touch.clientY }
  }, [])

  const onBodyTouchMove = useCallback((event) => {
    const start = bodyTouchStartRef.current
    const touch = event.touches?.[0]
    if (!start || !touch) return

    const dx = touch.clientX - start.x
    const dy = touch.clientY - start.y

    if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
      blockTouchEvent(event)
      onBodySwipeMove?.(dx)
      return
    }

    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 6) {
      blockTouchEvent(event)
    }
  }, [onBodySwipeMove])

  const onBodyTouchEnd = useCallback((event) => {
    const start = bodyTouchStartRef.current
    const touch = event.changedTouches?.[0]
    bodyTouchStartRef.current = null
    if (!start || !touch) {
      onBodySwipeMove?.(0)
      return
    }

    const dx = touch.clientX - start.x
    const dy = touch.clientY - start.y

    if (Math.abs(dy) > Math.abs(dx)) {
      blockTouchEvent(event)
    }

    if (Math.abs(dx) < minBodySwipe || Math.abs(dx) < Math.abs(dy)) {
      onBodySwipeMove?.(0)
      return
    }

    if (dx > 0) onSwipePrev?.()
    else onSwipeNext?.()
    onBodySwipeMove?.(0)
  }, [minBodySwipe, onBodySwipeMove, onSwipeNext, onSwipePrev])

  return {
    onHeaderTouchStart,
    onHeaderTouchMove,
    onHeaderTouchEnd,
    onHeaderPointerDown,
    onHeaderPointerMove,
    onHeaderPointerEnd,
    onHeaderPointerCancel,
    onBodyTouchStart,
    onBodyTouchMove,
    onBodyTouchEnd,
  }
}
